import { __assign, __awaiter, __generator, __spreadArrays } from "tslib";
import isEqual from 'lodash.isequal';
import { FiasExtraFields, FiasFields } from '../types';
import { FiasAddressElement } from './FiasAddressElement';
import { FiasData } from './FiasData';

var FiasAddress =
/** @class */
function () {
  function FiasAddress(_a) {
    var _this = this;

    var _b = _a === void 0 ? {} : _a,
        fields = _b.fields,
        additionalFields = _b.additionalFields,
        errors = _b.errors,
        country = _b.country,
        foreignAddress = _b.foreignAddress;

    this.getAddressErrors = function () {
      return __assign({}, _this.errors);
    };

    this.getText = function (minField, skipTypes, connector) {
      if (skipTypes === void 0) {
        skipTypes = false;
      }

      if (connector === void 0) {
        connector = ', ';
      }

      if (_this.isEmpty) {
        return '';
      }

      var getElementText = function getElementText(element) {
        return element ? element.getText(skipTypes) : '';
      };

      var fields = minField ? FiasAddress.getParentFields(minField) : FiasAddress.MAIN_FIELDS;
      return fields.map(function (field) {
        return getElementText(_this.fields[field]);
      }).filter(Boolean).join(connector);
    };

    this.getFullText = function (withPostalCode) {
      if (withPostalCode === void 0) {
        withPostalCode = false;
      }

      var substrings = [];

      if (withPostalCode) {
        substrings.push(_this.postalCode);
      }

      if (_this.country) {
        substrings.push(_this.country.fullName);
      }

      substrings.push(_this.isForeign ? _this.foreignAddress : _this.getText());
      return substrings.filter(Boolean).join(', ');
    };

    this.isAllowedToFill = function (field) {
      var _a;

      var _b = _this.fields,
          region = _b.region,
          city = _b.city,
          settlement = _b.settlement,
          house = _b.house,
          garhouse = _b.garhouse;
      var hasCityOrSettlement = _this.isFiasData(city) || _this.isFiasData(settlement) || ((_a = region) === null || _a === void 0 ? void 0 : _a.isFederalCity);

      var hasHouse = _this.isFiasData(house) || _this.isFiasData(garhouse);

      var isCityDependentField = field === FiasFields.street || field === FiasFields.stead || field === FiasFields.house;
      var isHouseDependentField = field === FiasFields.room;

      if (!hasCityOrSettlement && isCityDependentField || !hasHouse && isHouseDependentField) {
        return false;
      }

      return true;
    };

    this.isFiasData = function (el) {
      var _a, _b;

      return !!((_b = (_a = el) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.fiasId);
    };

    this.isAllowedToSearchFullAddress = function (field) {
      return FiasAddress.FULL_ADDRESS_SEARCH_FIELDS.indexOf(field) > -1;
    }; // doesn't fully work on api side yet
    // @see https://yt.skbkontur.ru/issue/PS-1401


    this.isAllowedToSearchThroughChildrenOfDirectParent = function (field, fieldsSettings) {
      if (fieldsSettings) {
        for (var _i = 0, _a = FiasAddress.getParentFields(field); _i < _a.length; _i++) {
          var parentField = _a[_i];

          if (!FiasAddress.isFieldVisible(parentField, fieldsSettings)) {
            return false;
          }
        }
      }

      return FiasAddress.NOT_ONLY_DIRECT_PARENT_SEARCH_FIELDS.indexOf(field) > -1;
    };

    this.hasOnlyIndirectParent = function (field) {
      if (field) {
        var parents = FiasAddress.getParentFields(field);

        if (parents.length > 1) {
          var directParent = _this.fields[parents.pop()];

          return !directParent && parents.some(function (parent) {
            return Boolean(_this.fields[parent]);
          });
        }
      }

      return false;
    };

    this.getClosestParentFiasId = function (field) {
      if (!_this.isEmpty) {
        var parents = FiasAddress.getParentFields(field).slice().reverse();

        for (var _i = 0, parents_1 = parents; _i < parents_1.length; _i++) {
          var parentField = parents_1[_i];
          var parent_1 = _this.fields[parentField];

          if (parent_1) {
            return parent_1.fiasId;
          }
        }
      }
    };

    this.getFiasId = function () {
      if (!_this.isEmpty) {
        var fields = FiasAddress.MAIN_FIELDS.slice().reverse();

        for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
          var field = fields_1[_i];
          var element = _this.fields[field];

          if (element && element.fiasId) {
            return element.fiasId;
          }
        }
      }

      return '';
    };

    this.getParent = function (field) {
      if (_this.fields[field]) {
        var parents = FiasAddress.getParentFields(field).filter(function (f) {
          return Boolean(_this.fields[f]);
        });
        var closest = parents.pop();

        if (closest) {
          return _this.fields[closest];
        }
      }
    };

    this.verifyConsistency = function () {
      var verifiedFields = __assign({}, _this.fields);

      for (var _i = 0, _a = FiasAddress.MAIN_FIELDS; _i < _a.length; _i++) {
        var field = _a[_i];
        var element = _this.fields[field];

        if (element) {
          if (element.data) {
            var expectedParentFiasIds = element.data.parentFiasIds;

            var parent_2 = _this.getParent(field);

            if (!parent_2 || parent_2.fiasId && expectedParentFiasIds.includes(parent_2.fiasId)) {
              verifiedFields[field] = element;
              continue;
            }
          }

          return {
            address: FiasAddress.fieldsToResponse(verifiedFields),
            isValid: false,
            invalidLevel: field
          };
        }
      }

      return {
        address: FiasAddress.fieldsToResponse(verifiedFields),
        isValid: true
      };
    };

    this.getFiasPostalCode = function () {
      if (!_this.isEmpty) {
        var fields = FiasAddress.MAIN_FIELDS.slice().reverse();

        for (var _i = 0, fields_2 = fields; _i < fields_2.length; _i++) {
          var field = fields_2[_i];
          var element = _this.fields[field];

          if (element && element.data && element.data.postalCode) {
            return element.data.postalCode;
          }
        }
      }

      return '';
    };

    this.getAddressValue = function () {
      var fields = Object.keys(_this.fields);
      return fields.reduce(function (value, field) {
        var _a;

        var element = _this.fields[field];

        if (!element) {
          return value;
        }

        var name = element.name,
            fiasData = element.fiasData;
        return __assign(__assign({}, value), (_a = {}, _a[field] = __assign({
          name: name
        }, fiasData ? {
          data: fiasData
        } : {}), _a));
      }, {});
    }; // TODO: get fields usage from fieldsSettings


    this.getValue = function (withPostalCode) {
      if (withPostalCode === void 0) {
        withPostalCode = false;
      }

      var _a = _this,
          country = _a.country,
          foreignAddress = _a.foreignAddress;
      return __assign(__assign({
        address: _this.getAddressValue(),
        addressString: _this.getFullText(withPostalCode),
        addressErrors: _this.getAddressErrors(),
        fiasId: _this.getFiasId(),
        postalCode: _this.postalCode
      }, country ? {
        country: country
      } : {}), _this.isForeign ? {
        foreignAddress: foreignAddress
      } : {});
    };

    this.convertForVerification = function () {
      return FiasAddress.VERIFIABLE_FIELDS.reduce(function (value, field) {
        var _a;

        var element = _this.fields[field];

        if (!element) {
          return value;
        }

        return __assign(__assign({}, value), (_a = {}, _a[field] = __assign({}, element.verifiableData), _a));
      }, {});
    };

    this.getDiffFields = function (address, fieldsSettings) {
      var fields = fieldsSettings ? FiasAddress.filterVisibleFields(address.fields, fieldsSettings) : __assign({}, address.fields);

      for (var _i = 0, _a = FiasAddress.MAIN_FIELDS; _i < _a.length; _i++) {
        var field = _a[_i];
        var element = fields[field];

        if (element) {
          if (element.isEqualTo(_this.fields[field])) {
            delete fields[field];
          } else {
            break;
          }
        }
      }

      return fields;
    };

    this.fields = fields || {};
    this.additionalFields = additionalFields || {};
    this.errors = errors || {};
    this.country = country;
    this.foreignAddress = foreignAddress || '';
  }

  Object.defineProperty(FiasAddress.prototype, "isForeign", {
    get: function get() {
      return Boolean(this.country && !FiasAddress.IS_RUSSIA(this.country));
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(FiasAddress.prototype, "isEmpty", {
    get: function get() {
      var _this = this;

      return !FiasAddress.MAIN_FIELDS.some(function (field) {
        return Object.prototype.hasOwnProperty.call(_this.fields, field);
      });
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(FiasAddress.prototype, "hasErrors", {
    get: function get() {
      return Object.keys(this.errors).length > 0;
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(FiasAddress.prototype, "postalCode", {
    get: function get() {
      var value = this.additionalFields[FiasExtraFields.postalcode];
      return typeof value === 'string' ? value : this.getFiasPostalCode();
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(FiasAddress.prototype, "isPostalCodeValid", {
    get: function get() {
      var rusFormat = /^[\d]{6}$/;
      var foreignFormat = /^[\w.\-\s]*$/;
      return (!this.country || this.isForeign ? foreignFormat : rusFormat).test(this.postalCode);
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(FiasAddress.prototype, "isPostalCodeAltered", {
    get: function get() {
      var fiasPostalCode = this.getFiasPostalCode();
      return Boolean(fiasPostalCode) && this.postalCode !== fiasPostalCode;
    },
    enumerable: true,
    configurable: true
  });

  FiasAddress.prototype.hasError = function (field) {
    return Object.prototype.hasOwnProperty.call(this.errors, field);
  };

  FiasAddress.prototype.getError = function (field) {
    return this.errors[field];
  };

  FiasAddress.MAIN_FIELDS = [FiasFields.region, FiasFields.district, FiasFields.municipaldistrict, FiasFields.urbansettlement, FiasFields.city, FiasFields.intracityarea, FiasFields.settlement, FiasFields.planningstructure, FiasFields.street, FiasFields.stead, FiasFields.garhouse, FiasFields.house, FiasFields.room];
  FiasAddress.ADDITIONAL_FIELDS = [FiasExtraFields.postalcode];
  FiasAddress.ALL_FIELDS = __spreadArrays(FiasAddress.MAIN_FIELDS, FiasAddress.ADDITIONAL_FIELDS);
  FiasAddress.VERIFIABLE_FIELDS = [FiasFields.region, FiasFields.district, FiasFields.municipaldistrict, FiasFields.urbansettlement, FiasFields.city, FiasFields.intracityarea, FiasFields.settlement, FiasFields.planningstructure, FiasFields.street];
  FiasAddress.FULL_ADDRESS_SEARCH_FIELDS = [FiasFields.district, FiasFields.municipaldistrict, FiasFields.urbansettlement, FiasFields.city, FiasFields.intracityarea, FiasFields.settlement, FiasFields.planningstructure, FiasFields.street];
  FiasAddress.NOT_ONLY_DIRECT_PARENT_SEARCH_FIELDS = [FiasFields.district, FiasFields.municipaldistrict, FiasFields.urbansettlement, FiasFields.city, FiasFields.intracityarea, FiasFields.settlement, FiasFields.planningstructure, FiasFields.street];

  FiasAddress.IS_RUSSIA = function (country) {
    return isEqual(country, {
      shortName: 'Россия',
      fullName: 'Российская Федерация',
      code: '643'
    });
  };

  FiasAddress.responseToFields = function (response) {
    var fields = {};

    if (response) {
      FiasAddress.MAIN_FIELDS.forEach(function (field) {
        var fiasObject = response[field];

        if (fiasObject) {
          var data = new FiasData(fiasObject);
          fields[field] = new FiasAddressElement(field, data.name, data);
        }
      });
    }

    return fields;
  };

  FiasAddress.fieldsToResponse = function (fields) {
    var response = {};

    if (fields) {
      FiasAddress.MAIN_FIELDS.forEach(function (field) {
        var element = fields[field];

        if (element && element.fiasData) {
          response[field] = element.fiasData;
        }
      });
    }

    return response;
  };

  FiasAddress.createFromResponse = function (response, additionalFields, country) {
    return new FiasAddress({
      fields: FiasAddress.responseToFields(response),
      additionalFields: additionalFields,
      country: country
    });
  };

  FiasAddress.createFromAddressValue = function (addressValue, additionalFields, country) {
    var fields = {};

    if (addressValue) {
      FiasAddress.MAIN_FIELDS.forEach(function (field) {
        var addressField = addressValue[field];

        if (addressField) {
          var name_1 = addressField.name,
              data = addressField.data;
          fields[field] = new FiasAddressElement(field, name_1, data && new FiasData(data));
        }
      });
    }

    return new FiasAddress({
      fields: fields,
      additionalFields: additionalFields,
      country: country
    });
  };

  FiasAddress.createFromAddress = function (address, options) {
    var fields = address.fields,
        additionalFields = address.additionalFields,
        errors = address.errors,
        country = address.country,
        foreignAddress = address.foreignAddress;
    return new FiasAddress(__assign({
      fields: fields,
      additionalFields: additionalFields,
      errors: errors,
      country: country,
      foreignAddress: foreignAddress
    }, options));
  };

  FiasAddress.validate = function (address, locale, fieldsSettings) {
    var fields = address.fields;
    var errors = {};

    for (var _i = 0, _a = FiasAddress.MAIN_FIELDS; _i < _a.length; _i++) {
      var field = _a[_i];

      if (!FiasAddress.isFieldNeedValidation(field, fieldsSettings)) {
        break;
      }

      var element = fields[field];
      var error = '';

      if (element) {
        if (!element.data) {
          error = locale.addressNotVerified;
        }

        if (!address.isAllowedToFill(field)) {
          error = locale[field + "FillBefore"];
        }

        if (error) {
          errors[field] = error;
          break;
        }
      }
    }

    var needValidatePostalCode = FiasAddress.isFieldNeedValidation(FiasExtraFields.postalcode, fieldsSettings);

    if (Object.keys(errors).length === 0 && needValidatePostalCode) {
      if (address.postalCode && !address.isPostalCodeValid) {
        errors[FiasExtraFields.postalcode] = locale.postalcodeNotValid;
      } else if (address.isPostalCodeAltered) {
        errors[FiasExtraFields.postalcode] = locale.postalcodeNotFound;
      }
    }

    return FiasAddress.createFromAddress(address, {
      errors: errors
    });
  };

  FiasAddress.verify = function (address, response) {
    if (address.isEmpty) {
      return address;
    }

    var addressResponse = response.address,
        invalidLevel = response.invalidLevel;
    var verifiedAddress = FiasAddress.createFromAddress(address, {
      fields: __assign(__assign({}, address.fields), FiasAddress.responseToFields(addressResponse))
    });
    var invalidField = invalidLevel || verifiedAddress.verifyConsistency().invalidLevel;

    if (invalidField) {
      return FiasAddress.removeFiasData(verifiedAddress, __spreadArrays([invalidField], FiasAddress.getChildFields(invalidField)));
    }

    return verifiedAddress;
  }; // TODO: Hide invisible fields without removing them.
  // (removing can break the verification)


  FiasAddress.filterVisibleFields = function (fields, fieldsSettings) {
    var filteredFields = {};
    var field;

    for (field in fields) {
      if (FiasAddress.isFieldVisible(field, fieldsSettings)) {
        filteredFields[field] = fields[field];
      }
    }

    return filteredFields;
  };

  FiasAddress.isFieldVisible = function (field, fieldsSettings) {
    var settings = fieldsSettings[field];
    return Boolean(settings && settings.visible);
  };

  FiasAddress.isFieldNeedValidation = function (field, fieldsSettings) {
    var setting = fieldsSettings[field];

    if (!setting) {
      return true;
    }

    return Boolean(setting.validation);
  };

  FiasAddress.getParentFields = function (field) {
    var index = FiasAddress.MAIN_FIELDS.indexOf(field);
    return index > -1 ? FiasAddress.MAIN_FIELDS.slice(0, index) : [];
  };

  FiasAddress.getChildFields = function (field) {
    var index = FiasAddress.MAIN_FIELDS.indexOf(field);
    return index > -1 ? FiasAddress.MAIN_FIELDS.slice(index + 1) : [];
  };

  FiasAddress.removeFiasData = function (address, fields) {
    if (fields === void 0) {
      fields = FiasAddress.MAIN_FIELDS;
    }

    var addressFields = __assign({}, address.fields);

    for (var _i = 0, fields_3 = fields; _i < fields_3.length; _i++) {
      var field = fields_3[_i];
      var element = addressFields[field];

      if (element) {
        element.removeData();
      }
    }

    return FiasAddress.createFromAddress(address, {
      fields: addressFields
    });
  };

  FiasAddress.getAddress = function (api, value, fieldsSettings) {
    return __awaiter(void 0, void 0, void 0, function () {
      var address, addressString, fiasId, postalCode, country, foreignAddress, additionalFields, searchOptions, addressValue, _a, success, data, addressResponse;

      var _b;

      return __generator(this, function (_c) {
        switch (_c.label) {
          case 0:
            if (!value) {
              return [2
              /*return*/
              , new FiasAddress()];
            }

            address = value.address, addressString = value.addressString, fiasId = value.fiasId, postalCode = value.postalCode, country = value.country, foreignAddress = value.foreignAddress;
            additionalFields = {};
            searchOptions = {};

            if (postalCode) {
              additionalFields[FiasExtraFields.postalcode] = postalCode;
            }

            if (country && !FiasAddress.IS_RUSSIA(country)) {
              return [2
              /*return*/
              , new FiasAddress({
                country: country,
                foreignAddress: foreignAddress,
                additionalFields: (_b = {}, _b[FiasExtraFields.postalcode] = postalCode, _b)
              })];
            }

            if (address) {
              addressValue = fieldsSettings ? FiasAddress.filterVisibleFields(address, fieldsSettings) : address;
              return [2
              /*return*/
              , FiasAddress.createFromAddressValue(addressValue, additionalFields, country)];
            }

            if (fiasId) {
              searchOptions = {
                fiasId: fiasId
              };
            }

            if (addressString) {
              searchOptions = {
                searchText: addressString,
                limit: 1
              };
            }

            if (!api) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , api.search(searchOptions)];

          case 1:
            _a = _c.sent(), success = _a.success, data = _a.data;

            if (success && data && data.length) {
              addressResponse = fieldsSettings ? FiasAddress.filterVisibleFields(data[0], fieldsSettings) : data[0];
              return [2
              /*return*/
              , FiasAddress.createFromResponse(addressResponse, additionalFields, country)];
            }

            _c.label = 2;

          case 2:
            return [2
            /*return*/
            , new FiasAddress({
              country: country
            })];
        }
      });
    });
  };

  return FiasAddress;
}();

export { FiasAddress };